'use strict';

import allNutrients from '../tables/nutrients';

/**
 * Constants for macro nutrient IDs
 */
export const MACRO_NUTRIENTS = {
    CALORIES: '208',
    PROTEIN: '203',
    FAT: '204',
    CARBS: '205',
};

export const MACRO_NUTRIENT_ARRAY = [
    MACRO_NUTRIENTS.PROTEIN,
    MACRO_NUTRIENTS.FAT,
    MACRO_NUTRIENTS.CARBS
];

export const EXPECTED_TOTAL_PERCENTAGE = 100;

/**
 * Validates if min and max values are valid numbers
 * @param {string|number} min - Minimum value
 * @param {string|number} max - Maximum value
 * @returns {Object} Object with boolean flags for min and max validity
 */
export function hasMinMax(min, max) {
    const fMin = parseFloat(min);
    const fMax = parseFloat(max);
    
    const sMin = (String(min) || '').trim();
    const sMax = (String(max) || '').trim();
    
    return {
        min: sMin.length > 0 && typeof fMin === 'number' && !isNaN(fMin),
        max: sMax.length > 0 && typeof fMax === 'number' && !isNaN(fMax), // Fixed bug
    };
}

/**
 * Calculates macro percentages from gram values
 * @param {number} min - Minimum grams
 * @param {number} max - Maximum grams
 * @param {string} nutrNo - Nutrient ID
 * @param {number} kcalMin - Minimum calories
 * @param {number} kcalMax - Maximum calories
 * @returns {Object} Object with perMin and perMax as decimal percentages
 */
export function calculateMacroPercentages(min, max, nutrNo, kcalMin, kcalMax) {
    const nutrient = allNutrients[nutrNo];
    if (!nutrient?.calories_per_gram || !kcalMin || !kcalMax) {
        return { perMin: null, perMax: null };
    }
    
    // min=0 is valid, don't treat as falsy. Follow NutrientEditor pattern.
    // Return 0 for min=0, null only when min is not provided
    const perMin = (min !== null && min !== undefined) ? 
        (kcalMin && kcalMin !== 0 ? (min * nutrient.calories_per_gram) / kcalMin : 0) : null;
    const perMax = (max !== null && max !== undefined) ? 
        (kcalMax && kcalMax !== 0 ? (max * nutrient.calories_per_gram) / kcalMax : 0) : null;
    
    return { perMin, perMax };
}

/**
 * Rounds a percentage value according to the mode
 * @param {number} value - Value to round
 * @param {string} mode - Either 'kcal' or 'g/kg'
 * @returns {number} Rounded value
 */
export function roundPercentage(value, mode = 'kcal') {
    if (!value && value !== 0) return value;
    
    if (mode === 'g/kg') {
        return Math.round(value * 10) / 10; // One decimal place for g/kg
    }
    return Math.round(value); // No decimals for kcal percentages
}

/**
 * Computes percentage and spread from min/max values
 * @param {number} perMin - Minimum percentage (as decimal)
 * @param {number} perMax - Maximum percentage (as decimal)
 * @param {string} mode - Either 'kcal' or 'g/kg'
 * @param {boolean} skipRounding - If true, returns raw values without rounding
 * @returns {Object} Object with percentage and spread
 */
export function computePercentageAndSpread(perMin, perMax, mode = 'kcal', skipRounding = false) {
    let percentage = '';
    let spread = null;
    
    if (perMin !== null && perMax !== null) {
        const rawPercentage = mode === 'g/kg' 
            ? (perMin + perMax) / 2 
            : (perMin + perMax) / 2 * 100;
            
        percentage = skipRounding ? rawPercentage : roundPercentage(rawPercentage, mode);
        spread = (perMax - perMin) / 2;
    } else if (perMin !== null && perMax === null) {
        const rawPercentage = mode === 'g/kg' ? perMin : perMin * 100;
        percentage = skipRounding ? rawPercentage : roundPercentage(rawPercentage, mode);
        spread = null;
    } else if (perMin === null && perMax !== null) {
        const rawPercentage = mode === 'g/kg' ? perMax : perMax * 100;
        percentage = skipRounding ? rawPercentage : roundPercentage(rawPercentage, mode);
        spread = null;
    }
    
    return { percentage, spread };
}

/**
 * Formats a value for display, optionally showing exact decimal values
 * @param {number} value - Value to format
 * @param {boolean} showExact - If true, shows exact decimal values
 * @returns {string|number} Formatted value
 */
export function formatMacroValue(value, showExact = false) {
    if (showExact && typeof value === 'number') {
        // Show exact value with up to 2 decimal places if needed
        return value % 1 === 0 ? value : value.toFixed(2).replace(/\.?0+$/, '');
    }
    // Regular formatting
    if (Number.isInteger(value)) {
        return value;
    }
    return value.toFixed(1);
}

/**
 * Gets the display name for a macro nutrient
 * @param {string} macro - Macro identifier (protein, fat, carbs)
 * @returns {string} Display name
 */
export function getMacroDisplayName(macro) {
    const names = {
        protein: 'Protein',
        fat: 'Fat',
        carbs: 'Carbohydrates'
    };
    return names[macro] || macro;
}

/**
 * Checks if a nutrient is a macro nutrient
 * @param {string} nutrNo - Nutrient ID
 * @returns {boolean} True if it's a macro nutrient
 */
export function isMacroNutrient(nutrNo) {
    return MACRO_NUTRIENT_ARRAY.includes(nutrNo);
}